import { useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useShallow } from "zustand/shallow";
import { useForm } from "react-hook-form";
import {
  useGetMerchantPaymentAccounts,
  useGetMerchantWalletBalance,
  useMerchantDisbursementsCharges,
  useMerchantInitiateWithdrawal,
  useAuthorizeDisbursement,
  useResendDisbursementOtp,
  useGetDisbursement,
} from "../../../hooks/queries/useMerchantQueries";
import { usePollDisbursement } from "../shared/usePollDisbursement";
import { useOtpInactivityTimer } from "../../shared/useOtpInactivityTimer";
import { useResendTimer } from "../../../hooks/useResendTimer";
import useFundsTransferStore from "../../../stores/fundsTransferStore";
import { formatKES } from "../../../utils/transactionutils";
import { toastService } from "../../../providers";
import { extractAxiosErrorMessage } from "../../../services/utils/errorUtils";
import { isInvalidOtpMessage, applyOtpBlock } from "../../shared/otpErrorUtils";
import { useFundsTransferOtpBlockStore } from "../../../stores/useOtpBlockStore";
import {
  formatMobileMoneyAccount,
  formatBankAccount,
  formatWalletAccount,
} from "../utils/accountFormatters";
import {
  PAYOUT_ACCOUNT_TYPES,
  WITHDRAWAL_TYPE_LABEL,
} from "../config/fundsTransferConfig";

function getAccountTypesForWithdrawalType(accountsData, withdrawalType) {
  if (!accountsData) return [];
  switch (withdrawalType) {
    case PAYOUT_ACCOUNT_TYPES.MOBILE_MONEY:
      return (accountsData.mobileMoneyAccounts ?? []).map((acc) => ({
        id: acc.id,
        display: formatMobileMoneyAccount(acc),
        details: acc,
      }));
    case PAYOUT_ACCOUNT_TYPES.BANK:
      return (accountsData.bankAccounts ?? []).map((acc) => ({
        id: acc.id,
        display: formatBankAccount(acc),
        details: acc,
      }));
    case PAYOUT_ACCOUNT_TYPES.WALLET:
      return (accountsData.walletAccounts ?? []).map((acc) => ({
        id: acc.id,
        display: formatWalletAccount(acc),
        details: acc,
      }));
    default:
      return [];
  }
}

export function useWithdrawalFlow(withdrawalType) {
  const navigate = useNavigate();

  const {
    currentStep,
    otpSentTo,
    transactionFee,
    exciseDuty,
    totalAmount,
    transactionId,
    remarks,
    status,
    createdDate,
    newBalance,
  } = useFundsTransferStore(
    useShallow(
      ({
        currentStep,
        otpSentTo,
        transactionFee,
        exciseDuty,
        totalAmount,
        transactionId,
        remarks,
        status,
        createdDate,
        newBalance,
      }) => ({
        currentStep,
        otpSentTo,
        transactionFee,
        exciseDuty,
        totalAmount,
        transactionId,
        remarks,
        status,
        createdDate,
        newBalance,
      }),
    ),
  );

  const setCurrentStep = useFundsTransferStore((state) => state.setCurrentStep);

  const { withdrawalAmount, selectedPayoutAccountId } =
    useFundsTransferStore.getState();

  const { control, watch, trigger } = useForm({
    defaultValues: {
      amount: withdrawalAmount || "",
      selectedPayoutAccountId: selectedPayoutAccountId || "",
    },
    mode: "onTouched",
  });

  const watchedAmount = watch("amount");
  const watchedSelectedPayoutAccountId = watch("selectedPayoutAccountId");

  const {
    control: otpControl,
    handleSubmit: handleOtpSubmit,
    watch: watchOtp,
  } = useForm({ defaultValues: { otp: "" } });

  const otpValue = watchOtp("otp") ?? "";

  const { data: walletBalanceData, isError: isBalanceError } =
    useGetMerchantWalletBalance();
  const walletBalance = walletBalanceData?.balance ?? null;

  const {
    data: accountsData,
    isLoading: isAccountsLoading,
    isFetching: isAccountsFetching,
    isError: isAccountsError,
    refetch: refetchAccounts,
  } = useGetMerchantPaymentAccounts();

  const { mutate: fetchCharges, isPending: isLoadingCharges } =
    useMerchantDisbursementsCharges();

  const { mutate: initiateWithdrawal, isPending: isInitiating } =
    useMerchantInitiateWithdrawal();

  const { mutate: authorizeDisburse, isPending: isAuthorizing } =
    useAuthorizeDisbursement();

  const { mutate: resendOtp } = useResendDisbursementOtp();

  // Only fetch on Step 4 — polling is owned by usePollDisbursement, and this
  // data is not needed until the result screen. Handles the edge case where
  // the status resolves between the last poll and the step 4 transition.
  const {
    data: disbursementData,
    isLoading: isLoadingDisbursement,
    isError: isDisbursementError,
    refetch: refetchDisbursement,
  } = useGetDisbursement(currentStep >= 4 ? transactionId : null, {
    enablePolling: false,
  });

  useEffect(() => {
    if (disbursementData) {
      useFundsTransferStore.getState().setDisbursementResult(disbursementData);
    }
  }, [disbursementData]);

  const { isPolling, startPolling } = usePollDisbursement();

  const {
    secondsLeft: resendSecondsLeft,
    startTimer: startResendTimer,
    isActive: isResendCooldownActive,
  } = useResendTimer(60);

  const handleOtpTimeout = useCallback(() => {
    navigate("/funds-transfer", {
      state: { fundsTransferBannerReason: "otp_session_expired" },
    });
  }, [navigate]);

  const { resetTimer: resetOtpTimer } = useOtpInactivityTimer({
    otpValue,
    isAuthorizing,
    isPolling,
    isActive: currentStep === 3,
    onTimeout: handleOtpTimeout,
  });

  const accountOptions = getAccountTypesForWithdrawalType(
    accountsData,
    withdrawalType,
  );

  const payoutAccountOptions = useMemo(
    () =>
      accountOptions.map((acc) => ({
        value: acc.id,
        label: acc.display,
        disabled: !acc?.details?.active,
      })),
    [accountOptions],
  );

  const selectedPayoutAccount = useMemo(() => {
    if (!watchedSelectedPayoutAccountId) return null;
    return (
      accountOptions.find((acc) => acc.id === watchedSelectedPayoutAccountId) ??
      null
    );
  }, [watchedSelectedPayoutAccountId, accountOptions]);

  const summaryItems = [
    {
      label: "Withdrawal Type",
      value: WITHDRAWAL_TYPE_LABEL[withdrawalType] ?? "—",
    },
    {
      label: "Destination Account",
      value: selectedPayoutAccount
        ? (selectedPayoutAccount.details?.accountName ??
          selectedPayoutAccount.display)
        : "—",
    },
    {
      label: "Amount",
      value: formatKES(watchedAmount),
    },
    {
      label: "Available Balance",
      value: formatKES(walletBalance),
    },
  ];

  const isFormLocked =
    isAccountsLoading ||
    isAccountsFetching ||
    isLoadingCharges ||
    payoutAccountOptions.length === 0;

  useEffect(() => {
    useFundsTransferStore.getState().setWithdrawalDraft({
      withdrawalAmount: watchedAmount,
      selectedPayoutAccountId: watchedSelectedPayoutAccountId,
    });
  }, [watchedAmount, watchedSelectedPayoutAccountId]);

  const handleStep1Next = async () => {
    const isValid = await trigger(["amount", "selectedPayoutAccountId"]);
    if (!isValid) return;

    fetchCharges(
      {
        amount: Number(watchedAmount),
        payoutAccountId: watchedSelectedPayoutAccountId,
        payoutAccountType: withdrawalType,
      },
      {
        onSuccess: (data) => {
          useFundsTransferStore.getState().setWithdrawalCharges({
            transactionFee: data.charges,
            exciseDuty: data.exciseDuty,
            totalAmount: data.totalAmount,
            newBalance: data.newBalance,
          });
          setCurrentStep(2);
        },
        onError: (error) => {
          toastService.error(extractAxiosErrorMessage(error));
        },
      },
    );
  };

  const handleStep2Back = () => {
    useFundsTransferStore.getState().setCurrentStep(1);
  };

  const handleStep2Confirm = () => {
    const { withdrawalAmount, selectedPayoutAccountId } =
      useFundsTransferStore.getState();

    initiateWithdrawal(
      {
        amount: Number(withdrawalAmount),
        payoutAccountId: selectedPayoutAccountId,
        payoutAccountType: withdrawalType,
      },
      {
        onSuccess: (data) => {
          useFundsTransferStore.getState().setWithdrawalInitiation({
            transactionId: data.transactionId,
            otpSentTo: data.otpSentTo,
          });
          startResendTimer();
          setCurrentStep(3);
        },
        onError: (error) => {
          toastService.error(extractAxiosErrorMessage(error));
        },
      },
    );
  };

  const handleResendOtp = () => {
    const { transactionId } = useFundsTransferStore.getState();
    if (!transactionId) return;
    resetOtpTimer();
    startResendTimer();
    resendOtp(transactionId, {
      onError: () => {
        toastService.error("Failed to resend code. Please try again.");
      },
    });
  };

  const handleStep3Submit = ({ otp }) => {
    authorizeDisburse(
      { otpCode: otp },
      {
        onSuccess: (data) => {
          useFundsTransferStore.getState().setDisbursementResult(data);
          startPolling();
        },
        onError: (err) => {
          if (!err.response) {
            toastService.error(
              "Request failed. Please check your connection and try again.",
            );
            return;
          }
          const message = extractAxiosErrorMessage(err);
          if (isInvalidOtpMessage(message)) {
            toastService.error(
              "Invalid code entered. Please check and try again.",
            );
            return;
          }
          if (message.toLowerCase().includes("validation blocked")) {
            applyOtpBlock(err.response, useFundsTransferOtpBlockStore);
            navigate("/funds-transfer");
            return;
          }
          useFundsTransferStore.getState().setWithdrawalFailed(message);
          useFundsTransferStore.getState().setCurrentStep(4);
        },
      },
    );
  };

  return {
    currentStep,
    control,
    payoutAccountOptions,
    isAccountsLoading,
    isAccountsFetching,
    isAccountsError,
    refetchAccounts,
    walletBalance,
    isBalanceError,
    isLoadingCharges,
    isFormLocked,
    onNext: handleStep1Next,
    selectedPayoutAccount,
    withdrawalAmount: watchedAmount,
    watchedSelectedPayoutAccountId,
    transactionFee,
    exciseDuty,
    totalAmount,
    handleStep2Back,
    handleStep2Confirm,
    isInitiating,
    otpControl,
    handleOtpSubmit,
    otpValue,
    otpSentTo,
    handleStep3Submit,
    isAuthorizing,
    handleResendOtp,
    resendSecondsLeft,
    isResendCooldownActive,
    isPolling,
    summaryItems,
    transactionId,
    remarks,
    status,
    createdDate,
    newBalance,
    isLoadingDisbursement,
    isDisbursementError,
    refetchDisbursement,
  };
}
